import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { describeWindow, formatChargeCode, formatRate, TARIFF_KIND_LABELS, type TariffKind, type TouPeriod } from '@pulsepoint/shared';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useCancelTariff, useReplaceChargeCode, useTariffs, useUpdateCharger, type Charger, type Tariff, type TariffSummary } from '../../api/chargers';
import { usePermission } from '../../auth/permissions';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { FormSection } from '../../components/form/FieldRow';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/format';
import { colours, radius } from '../../theme';
import { ChooseStickersDialog, type SocketSticker } from '../qrStickers/ChooseStickersDialog';
import { SetPriceDialog } from './SetPriceDialog';
import { chargerName } from './status';

/** "$0.55 per kWh, including GST" */
export function describePrice(t: { pricePerKwh: number; gstInclusive: boolean }) {
  return `${formatRate(t.pricePerKwh)} per kWh${t.gstInclusive ? ', including GST' : ', plus GST'}`;
}

/** The hours a price was set with differ from the site's schedule now (or one was added or removed). */
export function scheduleChanged(t: TariffSummary | null, schedule: { periods: TouPeriod[] } | null): boolean {
  if (!t) return false;
  const had = t.periods && t.periods.length > 1 ? t.periods.map((p) => ({ name: p.name, windows: p.windows })) : null;
  const now = schedule && schedule.periods.length > 1 ? schedule.periods.map((p) => ({ name: p.name, windows: p.windows })) : null;
  return JSON.stringify(had) !== JSON.stringify(now);
}

/**
 * All of a charger's prices: the public price guests pay (public charging, with its switch) and
 * the private price the organisation's RFID cards are charged. Both include Infinity EV's
 * rate; only the public one has card fees. Prices that have started are never edited, so past
 * sessions and reports keep the price they ran at. Managers see it all, read only.
 */
export function PricingTab({ charger: c }: { charger: Charger }) {
  const canSetPrice = usePermission('tariffs.edit') && c.registration === 'accepted';
  const canEdit = usePermission('chargers.edit') && c.registration === 'accepted';
  const schedule = c.rateSchedule && c.rateSchedule.periods.length > 1 ? c.rateSchedule : null;
  const stale = scheduleChanged(c.tariff, c.rateSchedule) || scheduleChanged(c.privateTariff, c.rateSchedule);

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      {schedule && (
        <Typography color="textSecondary">
          Peak and off-peak hours: <strong style={{ fontWeight: 600, color: colours.nearBlack }}>{schedule.name}</strong>, chosen on the site.
        </Typography>
      )}
      {stale && canSetPrice && (
        <Alert severity="warning">
          The site's peak and off-peak hours have changed since these prices were set. They keep their old hours until you set new prices.
        </Alert>
      )}

      <PriceSection charger={c} kind="public" canSetPrice={canSetPrice}>
        <PublicSwitch charger={c} canEdit={canEdit} />
      </PriceSection>
      <PriceSection charger={c} kind="private" canSetPrice={canSetPrice} />
    </Box>
  );
}

function PriceSection({ charger: c, kind, canSetPrice, children }: { charger: Charger; kind: TariffKind; canSetPrice: boolean; children?: React.ReactNode }) {
  const dialog = useDialog();
  const toast = useToast();
  const cancel = useCancelTariff();
  const history = useTariffs(c.id, kind);
  const [showAll, setShowAll] = useState(false);
  const now = kind === 'public' ? c.tariff : c.privateTariff;
  const next = kind === 'public' ? c.nextTariff : c.nextPrivateTariff;
  const rows = history.data ?? [];

  return (
    <Box component="section" aria-label={TARIFF_KIND_LABELS[kind]} sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Typography component="h3" sx={{ fontSize: 18, fontWeight: 500 }}>
          {kind === 'public' ? 'Public price.' : 'Private price.'}
        </Typography>
        <Typography color="textSecondary">
          {kind === 'public'
            ? 'What guests pay when they scan the QR code: Infinity EV’s rate and card fees come out of it.'
            : 'What drivers with this organisation’s RFID cards are charged, including Infinity EV’s rate. No card fees.'}
        </Typography>
      </Box>
      {children}
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: `${radius.card}px` }}>
        {now ? (
          <PriceView t={now} />
        ) : (
          <>
            <Typography sx={{ fontSize: 20, fontWeight: 500 }}>No {kind} price yet</Typography>
            <Typography color="textSecondary" sx={{ mt: 0.5 }}>
              {kind === 'private'
                ? "RFID cards can't start a session here until there's a private price. Set it to $0.00 for free charging."
                : 'Public charging can’t be used here until there’s a public price.'}
            </Typography>
          </>
        )}
        {next && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: `1px solid ${colours.hairline}` }}>
            <Typography sx={{ flex: 1, minWidth: 220 }}>
              Changes to <strong style={{ fontWeight: 600 }}>{rateSummary(next)}</strong> on {formatDateTime(next.validFrom)}.
            </Typography>
            {canSetPrice && (
              <Button
                size="small"
                onClick={async () => {
                  const ok = await dialog.confirm({ title: 'Cancel this price change?', message: `The ${kind} price stays at ${now ? rateSummary(now) : 'what it is'}.`, confirmLabel: 'Cancel the change', cancelLabel: 'Keep it' });
                  if (!ok) return;
                  try {
                    await cancel.mutateAsync(next.id);
                    toast('The price change was cancelled.');
                  } catch (e) {
                    toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                  }
                }}
              >
                Cancel the change
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {canSetPrice && (
        <Box>
          <Button variant={kind === 'public' ? 'contained' : 'outlined'} onClick={() => void dialog.open(SetPriceDialog, { charger: c, kind })}>
            Set a new {kind} price
          </Button>
        </Box>
      )}

      {rows.length > 0 && (
        <FormSection title={`${TARIFF_KIND_LABELS[kind]} history`}>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
            {(showAll ? rows : rows.slice(0, 4)).map((t, i) => (
              <Box component="li" key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2, py: 1.25, borderTop: i ? `1px solid ${colours.hairline}` : 0 }}>
                <Typography className="num" sx={{ fontWeight: 500, minWidth: 80 }}>{rateSummary(t)}</Typography>
                <Typography color="textSecondary" sx={{ flex: 1, minWidth: 200 }}>{period(t)}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {[
                    t.idleFeePerMinute ? `idle ${formatRate(t.idleFeePerMinute)}/min after ${t.idleGraceMinutes} min${t.idleFeeMax != null ? `, at most ${formatRate(t.idleFeeMax)}` : ''}` : 'no idle fee',
                    t.infinityevRatePerKwh != null ? `Infinity EV $${t.infinityevRatePerKwh.toFixed(4)}` : null,
                    t.gstInclusive ? null : 'plus GST',
                    t.createdBy ? `by ${t.createdBy}` : 'imported',
                  ].filter(Boolean).join(' · ')}
                </Typography>
                {t.state === 'current' ? <StatusLabel tone="success">Now</StatusLabel> : t.state === 'scheduled' ? <StatusLabel tone="neutral">Scheduled</StatusLabel> : null}
              </Box>
            ))}
          </Box>
          {rows.length > 4 && (
            <Box>
              <Button size="small" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show fewer' : `Show all ${rows.length}`}</Button>
            </Box>
          )}
        </FormSection>
      )}
    </Box>
  );
}

/** "$0.40" or, with peak and off-peak, "$0.40–$0.75". */
function rateSummary(t: { pricePerKwh: number; periods: TariffSummary['periods'] }) {
  if (!t.periods || t.periods.length < 2) return formatRate(t.pricePerKwh);
  const prices = t.periods.map((p) => p.pricePerKwh);
  return `${formatRate(Math.min(...prices))}–${formatRate(Math.max(...prices))}`;
}

/** The price in force: one big number, or a line per period with its hours. */
function PriceView({ t }: { t: TariffSummary }) {
  const idle = t.idleFeePerMinute ? ` · idle fee ${formatRate(t.idleFeePerMinute)} a minute if they stay plugged in more than ${t.idleGraceMinutes} minutes after charging stops${t.idleFeeMax != null ? `, at most ${formatRate(t.idleFeeMax)} a session` : ''}` : '';
  if (t.periods && t.periods.length > 1) {
    return (
      <>
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {t.periods.map((p, i) => (
            <Box key={p.name} sx={{ display: 'flex', gap: 2, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography className="num" sx={{ fontSize: 24, fontWeight: 500, minWidth: 90 }}>{formatRate(p.pricePerKwh)}</Typography>
              <Typography sx={{ fontWeight: 500 }}>{p.name}</Typography>
              <Typography color="textSecondary" variant="body2">{i === 0 ? 'All other times' : p.windows.map(describeWindow).join('; ')}</Typography>
            </Box>
          ))}
        </Box>
        <Typography color="textSecondary" sx={{ mt: 1.5 }}>
          Per kWh, {t.gstInclusive ? 'including GST' : 'plus GST'}
          {idle}
        </Typography>
      </>
    );
  }
  return (
    <>
      <Typography sx={{ fontSize: 32, lineHeight: 1.15, fontWeight: 500 }} className="num">
        {formatRate(t.pricePerKwh)} <Typography component="span" sx={{ fontSize: 18, color: colours.graphite }}>per kWh</Typography>
      </Typography>
      <Typography color="textSecondary" sx={{ mt: 0.5 }}>
        {t.gstInclusive ? 'Including GST' : 'Plus GST'}
        {idle}
      </Typography>
    </>
  );
}

/** Public charging, and its demo mode. It needs a public and a private price first. */
function PublicSwitch({ charger: c, canEdit }: { charger: Charger; canEdit: boolean }) {
  const update = useUpdateCharger();
  const replace = useReplaceChargeCode();
  const toast = useToast();
  const dialog = useDialog();
  const [error, setError] = useState('');
  const plugAndCharge = c.authMethod === 'plug_and_charge';
  const save = async (body: { publicCharging?: boolean; publicDemo?: boolean; chargeCodes?: SocketSticker[] }) => {
    setError('');
    // Turning it on: choose the QR sticker for each socket without one.
    const bare = Array.from({ length: c.connectorCount }, (_, i) => i + 1).filter((n) => !c.chargeCodes.some((x) => x.connectorId === n));
    if (body.publicCharging && bare.length) {
      const chargeCodes = await dialog.open(ChooseStickersDialog, {
        title: `QR stickers for ${chargerName(c)}`,
        lead: bare.length > 1 ? 'Guests scan the QR sticker on each socket to start charging.' : 'Guests scan the QR sticker on the charger to start charging.',
        connectorIds: bare,
        connectorCount: c.connectorCount,
        confirmLabel: 'Turn on public charging',
      });
      if (!chargeCodes) return;
      body = { ...body, chargeCodes };
    }
    try {
      await update.mutateAsync({ id: c.id, ...body });
      if (body.publicCharging !== undefined) toast(body.publicCharging ? `Public charging is on at ${chargerName(c)}.` : `Public charging is off at ${chargerName(c)}.`);
    } catch (e) {
      setError(e instanceof ApiError ? (e.fields.publicCharging ?? e.fields.chargeCodes ?? e.message) : 'Something went wrong.');
    }
  };
  // On, but a socket has no sticker (it was deleted): guests can't start there.
  const missing = c.publicCharging && !plugAndCharge ? Array.from({ length: c.connectorCount }, (_, i) => i + 1).filter((n) => !c.chargeCodes.some((x) => x.connectorId === n)) : [];
  const chooseMissing = async () => {
    const chosen = await dialog.open(ChooseStickersDialog, {
      title: `QR stickers for ${chargerName(c)}`,
      lead: 'Guests scan the QR sticker to start charging.',
      connectorIds: missing,
      connectorCount: c.connectorCount,
      confirmLabel: 'Use these stickers',
    });
    if (!chosen) return;
    setError('');
    try {
      for (const x of chosen) await replace.mutateAsync({ id: c.id, connectorId: x.connectorId, code: x.code });
      toast(`${chargerName(c)} has its QR sticker${chosen.length > 1 ? 's' : ''}.`);
    } catch (e) {
      setError(e instanceof ApiError ? (e.fields.code ?? e.message) : 'Something went wrong.');
    }
  };
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {missing.length > 0 && (
        <Alert
          severity="warning"
          role="alert"
          action={canEdit && <Button color="inherit" size="small" onClick={() => void chooseMissing()} disabled={replace.isPending}>Choose a sticker</Button>}
        >
          Public charging is on, but {c.connectorCount > 1 ? missing.map((n) => `socket ${String.fromCharCode(64 + n)}`).join(' and ') + (missing.length > 1 ? ' have' : ' has') : 'this charger has'} no QR sticker, so guests can't start charging there.
        </Alert>
      )}
      <FormControlLabel
        control={<Switch checked={c.publicCharging && !plugAndCharge} onChange={(e) => void save({ publicCharging: e.target.checked })} disabled={!canEdit || plugAndCharge || update.isPending} />}
        label="Public charging"
      />
      <Typography variant="body2" color="textSecondary" sx={{ pl: { sm: 6 }, mt: -1 }}>
        {plugAndCharge
          ? 'Not with plug and charge: choose RFID card in Settings to let guests pay.'
          : 'Guests scan the QR code on the charger and pay by card, Apple Pay or Google Pay. Needs a public and a private price.'}
      </Typography>
      {c.publicCharging && !plugAndCharge && (
        <Box sx={{ pl: { sm: 6 }, display: 'grid', gap: 1 }}>
          <Typography variant="body2" color="textSecondary">
            {c.chargeCodes.length ? (
              <>
                {c.chargeCodes.length > 1 ? 'Codes on the stickers: ' : 'Code on the sticker: '}
                {c.chargeCodes.map((x, i) => (
                  <Box component="span" key={x.connectorId}>
                    {i > 0 && ', '}
                    <Box component="span" className="num" sx={{ fontWeight: 600, color: colours.nearBlack, letterSpacing: '0.08em' }}>{formatChargeCode(x.code)}</Box>
                    {c.chargeCodes.length > 1 && ` (socket ${String.fromCharCode(64 + x.connectorId)})`}
                  </Box>
                ))}
                . Download them for printing in Settings, QR stickers.
              </>
            ) : (
              'Each socket gets a QR sticker when public charging is turned on.'
            )}
          </Typography>
          <FormControlLabel
            control={<Switch checked={c.publicDemo} onChange={(e) => void save({ publicDemo: e.target.checked })} disabled={!canEdit || update.isPending} />}
            label="Demo: guests go through the steps, but no card is charged and the charger doesn't start"
          />
        </Box>
      )}
      {error && <Alert severity="error" role="alert">{error}</Alert>}
    </Box>
  );
}

const period = (t: Tariff) =>
  t.state === 'scheduled' ? `From ${formatDateTime(t.validFrom)}` : t.validUntil ? `${formatDateTime(t.validFrom)} to ${formatDateTime(t.validUntil)}` : `Since ${formatDateTime(t.validFrom)}`;
